import React, { createContext, useContext, useEffect, useState } from 'react';
import { CellCogEvents } from '../utils/analytics';
import {
  clearConsentData,
  getConsentData,
  isBannerInCooldown,
  saveConsentData,
} from '../utils/cookieConsentStorage';
import { detectUserLocation } from '../utils/geoDetection';
import { clearAdClickIds, commitAdClickIds } from '../utils/firstTouch';

const CookieConsentContext = createContext();

export const CookieConsentProvider = ({ children }) => {
  // Geo-detection state
  const [isEU, setIsEU] = useState(null); // null = not detected yet
  const [country, setCountry] = useState(null);
  const [geoDetected, setGeoDetected] = useState(false);

  // Consent state
  const [consentGiven, setConsentGiven] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  // true when the banner was opened deliberately (footer/profile "Cookie
  // preferences") — the banner then reveals instantly instead of waiting for
  // the 10s/scroll delayed-reveal meant for unsolicited first-visit banners.
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Detect user location on mount
  useEffect(() => {
    const detectGeo = async () => {
      const geoData = await detectUserLocation();

      setIsEU(geoData.isEU);
      setCountry(geoData.country);
      setGeoDetected(true);

      // Check if user has existing consent choice
      const existingConsent = getConsentData();

      if (existingConsent) {
        // User has made a choice before
        setConsentGiven(true);
        setAnalyticsConsent(existingConsent.choices?.analytics || false);
        setShowBanner(false); // Don't show banner
        // Ad click IDs follow the recorded choice (Sep 27 2026, legal review pending).
        if (existingConsent.choices?.analytics) commitAdClickIds(); else clearAdClickIds();
      } else if (typeof navigator !== 'undefined' && navigator.globalPrivacyControl) {
        // Global Privacy Control (geo-split v3): the browser signal IS a valid
        // opt-out (California/Colorado enforce honoring it) — record it as a
        // rejection and never interrupt the visit with a banner.
        saveConsentData({
          isEU: geoData.isEU,
          country: geoData.country,
          bannerShown: false,
          gpc: true,
          choices: { essential: true, analytics: false },
        });
        setConsentGiven(true);
        setAnalyticsConsent(false);
        setShowBanner(false);
        clearAdClickIds(); // GPC = opt-out of ad attribution too
      } else {
        // New user or consent cleared
        setConsentGiven(false);
        setAnalyticsConsent(false);

        // Geo-split v3 (Aug 24, supersedes the v2 worldwide banner): the
        // banner shows ONLY in the strict opt-in bucket (isEU — historical
        // field name, now EU/UK/CH + CN + CA + KR per
        // geoDetection.STRICT_CONSENT_COUNTRIES), and even there an IGNORED
        // banner rests for 7 days (isBannerInCooldown) instead of returning
        // every visit — analytics stays denied for them until a real choice.
        // The quiet bucket (~84% of traffic) sees nothing on landing: notice =
        // footer Privacy Policy, control = Cookie preferences (openSettings),
        // analytics defaults granted via consent mode. Legal record:
        // work/cookie_consent/LEGAL_VERDICT_aug24.md (agent-side).
        setShowBanner(geoData.isEU && !isBannerInCooldown());
        // Opt-in regions keep ad click IDs held until an explicit accept.
        if (!geoData.isEU) commitAdClickIds();
      }
    };

    detectGeo();
  }, []);

  /**
   * Mirror a consent decision into PostHog (geo-split v3): PostHog's loader is
   * consent-gated at pageload, but a MID-SESSION rejection must also stop an
   * already-loaded instance. opt_out persists (localStorage) and the loader's
   * consent gate keeps PostHog off on future pageloads; a re-grant clears a
   * stale opt-out (if PostHog never booted this pageload, it loads on the
   * next one via the loader's gate).
   */
  const syncPostHogConsent = (granted) => {
    try {
      const ph = window.posthog;
      if (!ph) return;
      if (!granted && ph.opt_out_capturing) {
        ph.opt_out_capturing();
      } else if (granted && ph.has_opted_out_capturing && ph.has_opted_out_capturing() && ph.opt_in_capturing) {
        ph.opt_in_capturing({ captureEventName: null });
      }
    } catch (e) { /* posthog absent or stub — nothing to do */ }
  };

  /**
   * Handle user accepting all cookies
   */
  const acceptAll = () => {
    const consentData = {
      isEU,
      country,
      bannerShown: true,
      choices: {
        essential: true,
        analytics: true,
      },
    };

    saveConsentData(consentData);
    setAnalyticsConsent(true);
    setConsentGiven(true);
    setShowBanner(false);

    // Update Google Consent Mode
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
      });
    }

    syncPostHogConsent(true);
    commitAdClickIds();

    // Track consent choice
    CellCogEvents.cookieConsentChoice('accept_all', isEU, country);
  };

  /**
   * Handle user rejecting all non-essential cookies
   */
  const rejectAll = () => {
    const consentData = {
      isEU,
      country,
      bannerShown: true,
      choices: {
        essential: true,
        analytics: false,
      },
    };

    saveConsentData(consentData);
    setAnalyticsConsent(false);
    setConsentGiven(true);
    setShowBanner(false);

    // Explicitly deny analytics in Google Consent Mode. Now that the banner is
    // shown globally, non-EU users default to 'granted', so a rejection must
    // actively push 'denied' — it is no longer a no-op as it was when EU-only.
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
      });
    }

    syncPostHogConsent(false);
    clearAdClickIds();

    // Track consent choice (sent as cookieless ping if consent mode works)
    CellCogEvents.cookieConsentChoice('reject_all', isEU, country);
  };

  /**
   * Handle custom consent choices
   * @param {Object} choices - { analytics: boolean }
   */
  const updateChoices = (choices) => {
    const consentData = {
      isEU,
      country,
      bannerShown: true,
      choices: {
        essential: true,
        analytics: choices.analytics,
      },
    };

    saveConsentData(consentData);
    setAnalyticsConsent(choices.analytics);
    setConsentGiven(true);
    setShowBanner(false);

    // Update Google Consent Mode
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': choices.analytics ? 'granted' : 'denied',
      });
    }

    syncPostHogConsent(choices.analytics);
    if (choices.analytics) commitAdClickIds(); else clearAdClickIds();

    // Track consent choice
    CellCogEvents.cookieConsentChoice('customize', isEU, country);
  };

  /**
   * Apply a consent value decided ELSEWHERE (the account record, CEL-1678):
   * on login the server's cookie_consent overrides localStorage and is mirrored
   * into it, so a user who chose on any device is never re-asked here. Also
   * pushes the value into Google Consent Mode immediately.
   * @param {boolean} analytics
   */
  const applyExternalConsent = (analytics) => {
    saveConsentData({
      isEU,
      country,
      bannerShown: false,
      source: 'account',
      choices: { essential: true, analytics: !!analytics },
    });
    setAnalyticsConsent(!!analytics);
    setConsentGiven(true);
    setShowBanner(false);
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': analytics ? 'granted' : 'denied',
      });
    }
    syncPostHogConsent(!!analytics);
    if (analytics) commitAdClickIds(); else clearAdClickIds();
  };

  /**
   * Open cookie settings (re-show banner or modal)
   */
  const openSettings = () => {
    setSettingsOpen(true); // user asked: reveal instantly, no delay
    setShowBanner(true);
  };

  /**
   * Reset all consent (for testing)
   */
  const resetConsent = () => {
    clearConsentData();
    setConsentGiven(false);
    setAnalyticsConsent(false);
    setShowBanner(true); // Show banner to all users (global consent, v2)
  };

  const value = {
    // State
    isEU,
    country,
    geoDetected,
    consentGiven,
    analyticsConsent,
    showBanner,
    settingsOpen,

    // Actions
    acceptAll,
    rejectAll,
    updateChoices,
    openSettings,
    resetConsent,
    applyExternalConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

/**
 * Hook to use cookie consent context
 */
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
};

export default CookieConsentContext;
